/**
 * The behaviour behind the shared x-ui components, on every page (imported by app.js):
 *
 * - tfc.toast(message, { tone })      toasts (components/ui/toasts), also from Livewire's 'toast' event
 *                                     and a ->with('toast', ...) flash
 * - tfc.confirm({ message, ... })     the shared confirm dialog (components/ui/confirm-dialog), and
 *                                     data-confirm on forms, buttons and links
 * - tfc.modal(id)                     a Bootstrap Modal for x-ui.modal, also from Livewire's
 *                                     'modal-show' / 'modal-hide' events
 * - x-ui.tabs                         keeps the open tab in the URL hash
 * - data-copy="text"                  a button that copies the text and says so in a toast
 * - table.ui-table--stack             a hand-written table shown as cards on phones: each cell gets its
 *                                     column heading as data-label, unless the view set one
 * - .table-responsive                 marked has-more-left / has-more-right while columns are out of
 *                                     sight, for the edge fade and the pinned buttons' shadow
 *
 * Plain DOM, no Alpine: Alpine only loads on pages with a Livewire component.
 */
const TONES = {
    success: "fa-circle-check",
    danger: "fa-circle-exclamation",
    warning: "fa-triangle-exclamation",
    info: "fa-circle-info",
};

function toast(message, options = {}) {
    const container = document.getElementById("ui-toasts");
    if (!container || !message) {
        return;
    }

    const tone = TONES[options.tone] ? options.tone : "success";
    const element = document.createElement("div");
    element.className = `toast ui-toast ui-toast--${tone}`;
    element.setAttribute("role", tone === "danger" ? "alert" : "status");
    element.innerHTML = `
        <div class="d-flex align-items-start">
            <i class="fas ${TONES[tone]} ui-toast__icon" aria-hidden="true"></i>
            <div class="toast-body ui-toast__body"></div>
            <button type="button" class="btn-close me-2 mt-2" data-bs-dismiss="toast" aria-label="Close"></button>
        </div>`;
    element.querySelector(".ui-toast__body").textContent = message;
    container.appendChild(element);

    element.addEventListener("hidden.bs.toast", () => element.remove());
    window.bootstrap.Toast.getOrCreateInstance(element, {
        delay: options.delay ?? (tone === "danger" ? 8000 : 4000),
    }).show();
}

/**
 * Ask the user to confirm. Resolves true if they press the confirm button, false otherwise.
 */
function confirm(options = {}) {
    const dialog = document.getElementById("ui-confirm");
    if (!dialog) {
        return Promise.resolve(window.confirm(options.message ?? "Are you sure?"));
    }

    const ok = dialog.querySelector("[data-ui-confirm-ok]");
    const tone = options.tone ?? "primary";
    dialog.querySelector("#ui-confirm-title").textContent = options.title ?? "Are you sure?";
    dialog.querySelector("[data-ui-confirm-message]").textContent = options.message ?? "";
    ok.textContent = options.confirmLabel ?? "Confirm";
    dialog.querySelector("[data-ui-confirm-cancel]").textContent = options.cancelLabel ?? "Cancel";
    ok.className = `btn btn-${tone}`;

    const modal = window.bootstrap.Modal.getOrCreateInstance(dialog);

    return new Promise((resolve) => {
        let confirmed = false;
        const onOk = () => {
            confirmed = true;
            modal.hide();
        };
        ok.addEventListener("click", onOk, { once: true });
        dialog.addEventListener("shown.bs.modal", () => ok.focus(), { once: true });
        dialog.addEventListener(
            "hidden.bs.modal",
            () => {
                ok.removeEventListener("click", onOk);
                resolve(confirmed);
            },
            { once: true },
        );
        modal.show();
    });
}

function confirmOptions(element) {
    return {
        message: element.dataset.confirm,
        title: element.dataset.confirmTitle,
        confirmLabel: element.dataset.confirmLabel,
        cancelLabel: element.dataset.confirmCancelLabel,
        tone: element.dataset.confirmTone,
    };
}

/**
 * data-confirm: hold the click or submit, ask, then let it through. Listens in the capture phase so it
 * runs before any page handler, and marks the element so the replayed event isn't asked about again.
 */
function wireConfirmations() {
    document.addEventListener(
        "click",
        (event) => {
            const element = event.target.closest("[data-confirm]:not(form, select, option)");
            if (!element || element.dataset.confirmed === "1") {
                return;
            }

            event.preventDefault();
            event.stopImmediatePropagation();
            confirm(confirmOptions(element)).then((confirmed) => {
                if (!confirmed) {
                    return;
                }
                element.dataset.confirmed = "1";
                element.click();
                delete element.dataset.confirmed;
            });
        },
        true,
    );

    document.addEventListener(
        "submit",
        (event) => {
            const form = event.target;
            if (!form.matches?.("form[data-confirm]") || form.dataset.confirmed === "1") {
                return;
            }

            event.preventDefault();
            event.stopImmediatePropagation();
            const submitter = event.submitter;
            confirm(confirmOptions(form)).then((confirmed) => {
                if (!confirmed) {
                    return;
                }
                form.dataset.confirmed = "1";
                form.requestSubmit(submitter && submitter.form === form ? submitter : undefined);
                delete form.dataset.confirmed;
            });
        },
        true,
    );
}

function modal(id) {
    const element = document.getElementById(id);

    return element ? window.bootstrap.Modal.getOrCreateInstance(element) : null;
}

/**
 * x-ui.tabs: open the tab named in the URL hash, and put the open tab in the hash.
 */
function wireTabs() {
    const openFromHash = () => {
        const hash = window.location.hash.slice(1);
        if (!hash) {
            return;
        }
        const button = document.querySelector(`[data-ui-tabs] [data-bs-target="#${CSS.escape(hash)}"]`);
        if (button) {
            window.bootstrap.Tab.getOrCreateInstance(button).show();
        }
    };

    openFromHash();
    window.addEventListener("hashchange", openFromHash);

    document.addEventListener("shown.bs.tab", (event) => {
        if (event.target.closest("[data-ui-tabs]")) {
            history.replaceState(null, "", event.target.dataset.bsTarget);
        }
    });
}

/**
 * data-copy: copy the attribute's text to the clipboard (a link to send a client, a key).
 */
function wireCopyButtons() {
    document.addEventListener("click", (event) => {
        const button = event.target.closest("[data-copy]");
        if (!button) {
            return;
        }

        event.preventDefault();
        const text = button.dataset.copy;
        const copied = () => toast(button.dataset.copyMessage || "Copied", { tone: "success" });
        const failed = () => toast("Couldn't copy. Select the text and copy it yourself.", { tone: "danger" });

        // The clipboard API needs a secure page and permission; fall back to copying a selection.
        (navigator.clipboard ? navigator.clipboard.writeText(text) : Promise.reject())
            .then(copied)
            .catch(() => (copySelection(text) ? copied() : failed()));
    });
}

function copySelection(text) {
    const area = document.createElement("textarea");
    area.value = text;
    area.setAttribute("readonly", "");
    area.style.position = "fixed";
    area.style.opacity = "0";
    document.body.appendChild(area);
    area.select();
    let ok = false;
    try {
        ok = document.execCommand("copy");
    } catch {
        ok = false;
    }
    area.remove();

    return ok;
}

window.tfc = Object.assign(window.tfc || {}, { toast, confirm, modal });

// Livewire's $this->dispatch('toast', message: ..., tone: ...) arrives as a window event whose detail
// holds the named arguments.
window.addEventListener("toast", (event) => toast(event.detail?.message, event.detail ?? {}));
window.addEventListener("modal-show", (event) => modal(event.detail?.id)?.show());
window.addEventListener("modal-hide", (event) => modal(event.detail?.id)?.hide());

/**
 * Phones show a table.ui-table--stack as one card per row, labelling each value with the cell's data-label
 * (see _components.scss). Fill in the labels the view left out from the table's headings, honouring colspan.
 * A cell with data-label already (even an empty one, for a column of buttons) keeps it.
 */
function labelTable(table) {
    const labels = [];
    table.querySelectorAll(":scope > thead > tr:last-child > th").forEach((heading) => {
        const text = heading.textContent.trim().replace(/\s+/g, " ");
        for (let span = 0; span < (heading.colSpan || 1); span++) {
            labels.push(text);
        }
    });
    if (labels.length === 0) {
        return;
    }

    table.querySelectorAll(":scope > tbody > tr, :scope > tfoot > tr").forEach((row) => {
        let column = 0;
        for (const cell of row.children) {
            if (!cell.hasAttribute("data-label")) {
                cell.dataset.label = cell.colSpan > 1 ? "" : labels[column] ?? "";
            }
            column += cell.colSpan || 1;
        }
    });
}

const watchedTables = new WeakSet();

function labelStackedTables(root = document) {
    root.querySelectorAll("table.ui-table--stack").forEach((table) => {
        labelTable(table);

        // Rows a page script adds later (the cache split tool builds its rows) get labelled as they arrive.
        if (!watchedTables.has(table)) {
            watchedTables.add(table);
            const observer = new MutationObserver(() => labelTable(table));
            table.querySelectorAll(":scope > tbody, :scope > tfoot").forEach((section) => observer.observe(section, { childList: true }));
        }
    });
}

/**
 * A table wider than its card scrolls sideways inside .table-responsive. Mark the edges that have more
 * columns past them, so _components.scss can fade that edge and shadow the pinned column of buttons. The
 * marks follow scrolling, the card resizing and the table changing width (a column shown or hidden).
 */
const hintedScrollers = new WeakSet();
const scrollerSizes = typeof ResizeObserver === "function"
    ? new ResizeObserver((entries) => entries.forEach((entry) => markScrollEdges(entry.target.closest(".table-responsive"))))
    : null;

function markScrollEdges(scroller) {
    if (!scroller) {
        return;
    }
    const hidden = scroller.scrollWidth - scroller.clientWidth;
    scroller.classList.toggle("has-more-left", scroller.scrollLeft > 1);
    scroller.classList.toggle("has-more-right", scroller.scrollLeft < hidden - 1);
}

function wireScrollHints(root = document) {
    markScrollEdges(root.closest?.(".table-responsive"));
    root.querySelectorAll(".table-responsive").forEach((scroller) => {
        markScrollEdges(scroller);
        if (hintedScrollers.has(scroller)) {
            return;
        }
        hintedScrollers.add(scroller);
        scroller.addEventListener("scroll", () => markScrollEdges(scroller), { passive: true });
        scrollerSizes?.observe(scroller);
        const table = scroller.querySelector(":scope > table");
        if (table) {
            scrollerSizes?.observe(table);
        }
    });
}

wireConfirmations();
wireCopyButtons();

// A Livewire redraw drops the labels and edge marks added here (they aren't in the server's HTML), so add
// them again.
document.addEventListener("livewire:init", () => {
    window.Livewire.hook("morphed", ({ el }) => {
        labelStackedTables(el);
        wireScrollHints(el);
    });
});

/**
 * Bootstrap tooltips on [data-bs-toggle="tooltip"], including ones added later (delegated from the body).
 */
function wireTooltips() {
    document.querySelectorAll('[data-bs-toggle="tooltip"]').forEach((element) => new window.bootstrap.Tooltip(element));
    window.jQuery("body").tooltip({ selector: '[data-bs-toggle="tooltip"]' });
}

/**
 * The older pages' #settings-success / #settings-error boxes (layouts/authenticated_content_template): their
 * close buttons collapse them instead of removing them, so a page script can show them again.
 */
function wireSettingsAlerts() {
    ["settings-success", "settings-error"].forEach((id) => {
        const box = document.getElementById(id);
        if (!box) {
            return;
        }
        const collapse = window.bootstrap.Collapse.getOrCreateInstance(box, { toggle: false });
        box.querySelector(".btn-close")?.addEventListener("click", () => collapse.hide());
    });
}

document.addEventListener("DOMContentLoaded", () => {
    wireTabs();
    wireTooltips();
    wireSettingsAlerts();
    labelStackedTables();
    wireScrollHints();

    const flash = document.getElementById("ui-toasts")?.dataset.flash;
    if (flash) {
        const { message, tone } = JSON.parse(flash);
        toast(message, { tone });
    }
});
